import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../api';
import { Footer, TopBar } from '../components/TopBar';
import { SignIn } from '../components/SignIn';
import { RADII, type Radius } from '../state/url-state';

type Tab = 'routes' | 'newsletter' | 'listings';

interface RouteSummary {
  id: string;
  name: string;
  stop_count: number;
}

interface RouteStop {
  id: string;
  public_id: string;
  title: string;
  lat: number;
  lng: number;
  status: string;
  sort_order: number;
}

interface RouteDetail extends RouteSummary {
  stops: RouteStop[];
}

function RoutesTab() {
  const [routes, setRoutes] = useState<RouteSummary[] | null>(null);
  const [open, setOpen] = useState<RouteDetail | null>(null);
  const [err, setErr] = useState('');

  function load() {
    api
      .get<RouteSummary[]>('/routes')
      .then(setRoutes)
      .catch(() => setErr("Couldn't load your routes."));
  }

  useEffect(load, []);

  async function doRename(r: RouteSummary) {
    const name = window.prompt('Rename route', r.name);
    if (!name || !name.trim() || name.trim() === r.name) return;
    try {
      await api.patchJson(`/routes/${r.id}`, { name: name.trim() });
      load();
    } catch {
      setErr("Couldn't rename that route.");
    }
  }

  async function doDelete(r: RouteSummary) {
    if (!window.confirm(`Delete "${r.name}"?`)) return;
    try {
      await api.delete(`/routes/${r.id}`);
      if (open?.id === r.id) setOpen(null);
      load();
    } catch {
      setErr("Couldn't delete that route.");
    }
  }

  async function openRoute(r: RouteSummary) {
    try {
      const detail = await api.get<RouteDetail>(`/routes/${r.id}`);
      setOpen(detail);
    } catch {
      setErr("Couldn't load that route.");
    }
  }

  if (err) return <p className="error">{err}</p>;
  if (routes === null) return null;
  if (!routes.length) return <p className="hint">No saved routes yet. Build one from the map and save it.</p>;

  return (
    <div className="routes-tab">
      <ul className="routes-list">
        {routes.map((r) => (
          <li key={r.id}>
            <span>{r.name}</span>
            <span className="hint">{r.stop_count} stop{r.stop_count === 1 ? '' : 's'}</span>
            <button type="button" onClick={() => void openRoute(r)}>
              Open
            </button>
            <button type="button" onClick={() => void doRename(r)}>
              Rename
            </button>
            <button type="button" onClick={() => void doDelete(r)}>
              Delete
            </button>
          </li>
        ))}
      </ul>
      {open && (
        <div className="route-detail">
          <h3>{open.name}</h3>
          <ol>
            {open.stops.map((s) => (
              <li key={s.id} className={s.status !== 'published' ? 'muted' : undefined}>
                {s.title}
                {s.status !== 'published' && <span className="hint"> ({s.status})</span>}
              </li>
            ))}
          </ol>
          <button type="button" className="link" onClick={() => setOpen(null)}>
            Close
          </button>
        </div>
      )}
    </div>
  );
}

interface OwnedListing {
  public_id: string;
  title: string;
  status: string;
  start_at: string;
}

function ListingsTab() {
  const [listings, setListings] = useState<OwnedListing[] | null>(null);
  const [err, setErr] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<OwnedListing[]>('/my-listings')
      .then(setListings)
      .catch(() => setErr("Couldn't load your listings."));
  }, []);

  async function doEdit(l: OwnedListing) {
    setErr('');
    setEditingId(l.public_id);
    try {
      const { edit_url } = await api.get<{ edit_url: string }>(`/my-listings/${l.public_id}/edit-token`);
      window.location.href = edit_url;
    } catch {
      setErr("Couldn't open that listing for editing.");
      setEditingId(null);
    }
  }

  if (err) return <p className="error">{err}</p>;
  if (listings === null) return null;
  if (!listings.length) return <p className="hint">No listings yet. When you submit a sale while signed in, it'll show up here.</p>;

  return (
    <ul className="routes-list">
      {listings.map((l) => (
        <li key={l.public_id}>
          <span>{l.title}</span>
          <span className="hint">{l.status}</span>
          <a href={`/e/${l.public_id}`}>View</a>
          <button type="button" disabled={editingId === l.public_id} onClick={() => void doEdit(l)}>
            Edit
          </button>
        </li>
      ))}
    </ul>
  );
}

interface SubscriptionInfo {
  postal_code: string;
  radius_mi: number;
  status: string;
}

function NewsletterTab() {
  const [sub, setSub] = useState<SubscriptionInfo | null | undefined>(undefined);
  const [postalCode, setPostalCode] = useState('');
  const [radius, setRadius] = useState<Radius>(25);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  function load() {
    api
      .get<SubscriptionInfo | null>('/subscription')
      .then((s) => {
        setSub(s);
        if (s) {
          setPostalCode(s.postal_code);
          setRadius((RADII as readonly number[]).includes(s.radius_mi) ? (s.radius_mi as Radius) : 25);
        }
      })
      .catch(() => setErr("Couldn't load your subscription."));
  }

  useEffect(load, []);

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!/^\d{5}$/.test(postalCode)) {
      setErr('Enter a 5-digit ZIP code');
      return;
    }
    setBusy(true);
    setErr('');
    setMsg('');
    try {
      await api.patchJson('/subscription', { postal_code: postalCode, radius_mi: radius });
      setMsg('Saved.');
      load();
    } catch {
      setErr("Couldn't save that ZIP code.");
    } finally {
      setBusy(false);
    }
  }

  async function togglePause() {
    if (!sub) return;
    setBusy(true);
    setErr('');
    try {
      await api.patchJson('/subscription', { paused: sub.status !== 'paused' });
      load();
    } catch {
      setErr("Couldn't update your subscription.");
    } finally {
      setBusy(false);
    }
  }

  async function unsubscribe() {
    if (!window.confirm('Stop the weekly email?')) return;
    setBusy(true);
    setErr('');
    try {
      await api.postJson('/subscription/unsubscribe');
      load();
    } catch {
      setErr("Couldn't unsubscribe.");
    } finally {
      setBusy(false);
    }
  }

  if (err) return <p className="error">{err}</p>;
  if (sub === undefined) return null;

  if (!sub || sub.status === 'unsubscribed') {
    return (
      <form className="newsletter-tab" onSubmit={save}>
        <p className="hint">You&rsquo;re not getting the weekly email. Add your ZIP and radius to start.</p>
        <div className="row3">
          <input
            type="text"
            inputMode="numeric"
            pattern="\d{5}"
            required
            placeholder="ZIP"
            maxLength={5}
            value={postalCode}
            onChange={(e) => setPostalCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
          />
          <select value={radius} onChange={(e) => setRadius(Number(e.target.value) as Radius)}>
            {RADII.map((r) => (
              <option key={r} value={r}>
                {r} mi
              </option>
            ))}
          </select>
          <button className="primary" type="submit" disabled={busy || !postalCode}>
            Subscribe
          </button>
        </div>
      </form>
    );
  }

  return (
    <form className="newsletter-tab" onSubmit={save}>
      <p className="hint">
        {sub.status === 'paused' ? 'Paused. ' : ''}Weekly email for sales near {sub.postal_code}.
      </p>
      <div className="row3">
        <input
          type="text"
          inputMode="numeric"
          pattern="\d{5}"
          required
          placeholder="ZIP"
          maxLength={5}
          value={postalCode}
          onChange={(e) => setPostalCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
        />
        <select value={radius} onChange={(e) => setRadius(Number(e.target.value) as Radius)}>
          {RADII.map((r) => (
            <option key={r} value={r}>
              {r} mi
            </option>
          ))}
        </select>
        <button className="primary" type="submit" disabled={busy || !postalCode}>
          Save
        </button>
      </div>
      {msg && <p className="hint">{msg}</p>}
      <div className="row">
        <button type="button" disabled={busy} onClick={() => void togglePause()}>
          {sub.status === 'paused' ? 'Resume' : 'Pause'}
        </button>
        <button type="button" disabled={busy} onClick={() => void unsubscribe()}>
          Unsubscribe
        </button>
      </div>
    </form>
  );
}

export function Account() {
  const [user, setUser] = useState<{ email: string; display_name: string | null } | null | undefined>(undefined);
  const [tab, setTab] = useState<Tab>('routes');

  useEffect(() => {
    api.get<{ user: typeof user }>('/me').then((r) => setUser(r.user));
  }, []);

  if (user === undefined) return null;
  if (user === null) {
    return (
      <>
        <TopBar />
        <main className="page" id="main">
          <div className="card" style={{ maxWidth: 420, margin: '48px auto' }}>
            <h1>Sign in</h1>
            <SignIn />
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <TopBar />
      <main className="page account" id="main">
        <h1>Your account</h1>
        <p className="hint">{user.email}</p>
        <nav className="chips">
          {(['routes', 'newsletter', 'listings'] as Tab[]).map((t) => (
            <button key={t} aria-pressed={tab === t} onClick={() => setTab(t)}>
              {t[0]!.toUpperCase() + t.slice(1)}
            </button>
          ))}
        </nav>
        {tab === 'routes' && <RoutesTab />}
        {tab === 'newsletter' && <NewsletterTab />}
        {tab === 'listings' && <ListingsTab />}
      </main>
      <Footer />
    </>
  );
}
